import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useBlogPost, useCreateBlogPost, useUpdateBlogPost } from "@/hooks/useBlogPosts";
import { useBlogCategories } from "@/hooks/useBlogCategories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { RichTextEditor } from "@/components/editor/RichTextEditor";
import { useImageUpload } from "@/hooks/useImageUpload";

interface PostData {
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  cover_image_url: string;
  category_id: string | null;
}

export function BlogEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = Boolean(id);
  const { uploadImage, uploading: uploadingCover } = useImageUpload();

  const [publishNow, setPublishNow] = useState(false);
  const { data: categoriesData } = useBlogCategories();
  const categories = categoriesData?.categories ?? [];
  const { data: loadedPost, isLoading: loading, isError: loadError } = useBlogPost(id);
  const createPost = useCreateBlogPost();
  const updatePost = useUpdateBlogPost();
  const saving = createPost.isPending || updatePost.isPending;
  const [post, setPost] = useState<PostData>({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    cover_image_url: "",
    category_id: null,
  });

  // Hidrata o formulário quando o post carrega (edição). Erro → volta à lista.
  useEffect(() => {
    if (!id) return;
    if (loadError) {
      toast({
        title: "Erro ao carregar post",
        description: "Não foi possível carregar o post para edição.",
        variant: "destructive",
      });
      navigate("/admin/conteudo?tab=blog");
      return;
    }
    if (loadedPost) {
      setPost({
        title: loadedPost.title || "",
        slug: loadedPost.slug || "",
        excerpt: loadedPost.excerpt || "",
        content: loadedPost.content || "",
        cover_image_url: loadedPost.cover_image_url || "",
        category_id: loadedPost.category_id || null,
      });
      setPublishNow(!!loadedPost.published);
    }
  }, [id, loadedPost, loadError, navigate, toast]);

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)+/g, "");
  };

  const handleTitleChange = (value: string) => {
    setPost((prev) => ({
      ...prev,
      title: value,
      slug: isEditing ? prev.slug : generateSlug(value),
    }));
  };

  const handleContentChange = useCallback((content: string) => {
    setPost((prev) => ({ ...prev, content }));
  }, []);

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = await uploadImage(file);
    if (url) {
      setPost((prev) => ({ ...prev, cover_image_url: url }));
    }
  };

  const savePost = async (publish: boolean = false) => {
    if (!post.title.trim()) {
      toast({
        title: "Título obrigatório",
        description: "Por favor, insira um título para o post.",
        variant: "destructive",
      });
      return;
    }

    if (!post.content.trim()) {
      toast({
        title: "Conteúdo obrigatório",
        description: "Por favor, insira o conteúdo do post.",
        variant: "destructive",
      });
      return;
    }

    try {
      const postData = {
        title: post.title,
        slug: post.slug || generateSlug(post.title),
        excerpt: post.excerpt || null,
        content: post.content,
        cover_image_url: post.cover_image_url || null,
        category_id: post.category_id || null,
        published: publish,
        published_at: publish ? new Date().toISOString() : null,
      };

      if (isEditing) {
        await updatePost.mutateAsync({ id: id!, draft: postData });
      } else {
        await createPost.mutateAsync(postData);
      }

      toast({
        title: publish ? "Post publicado!" : "Post salvo!",
        description: publish
          ? "O post está agora visível no blog."
          : "O rascunho foi salvo com sucesso.",
      });

      navigate("/admin/conteudo?tab=blog");
    } catch (error) {
      console.error("Error saving post:", error);
      toast({
        title: "Erro ao salvar",
        description: error instanceof Error ? error.message : "Não foi possível salvar o post.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 animate-pulse rounded bg-muted"></div>
        <div className="h-96 animate-pulse rounded bg-muted"></div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-[1200px] flex-col gap-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0 rounded-[10px]"
            onClick={() => navigate("/admin/conteudo?tab=blog")}
            aria-label="Voltar"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="font-display text-[25px] font-bold tracking-[-0.03em]">
            {isEditing ? "Editar post" : "Novo post"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="h-[38px] rounded-[10px] px-[15px] text-[13px] font-semibold"
            onClick={() => savePost(publishNow)}
            disabled={saving}
          >
            {publishNow ? "Salvar" : "Salvar rascunho"}
          </Button>
          <Button
            className="h-[38px] rounded-[10px] px-[18px] text-[13px] font-semibold"
            onClick={() => savePost(true)}
            disabled={saving}
          >
            Publicar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[1.7fr_1fr]">
        {/* Main Editor */}
        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
          <div className="space-y-[18px] px-7 py-[26px]">
            <div className="space-y-2">
              <Label htmlFor="title" className="sr-only">
                Título
              </Label>
              <Input
                id="title"
                value={post.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="Título do artigo"
                className="h-auto border-none px-0 font-display text-[26px] font-bold tracking-[-0.03em] shadow-none placeholder:text-muted-foreground/60 focus-visible:ring-0"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="excerpt" className="sr-only">
                Resumo (opcional)
              </Label>
              <Textarea
                id="excerpt"
                value={post.excerpt}
                onChange={(e) => setPost((prev) => ({ ...prev, excerpt: e.target.value }))}
                placeholder="Breve descrição do artigo para listagem…"
                rows={2}
                className="resize-none border-none px-0 text-sm text-muted-foreground shadow-none focus-visible:ring-0"
              />
            </div>

            <div className="h-px bg-border" />

            <div>
              <span id="post-content-label" className="sr-only">
                Conteúdo
              </span>
              <RichTextEditor
                aria-labelledby="post-content-label"
                content={post.content}
                onChange={handleContentChange}
                placeholder="Comece a escrever seu artigo..."
              />
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="space-y-[15px] rounded-2xl border border-border bg-card px-[22px] py-5 shadow-card">
            <h2 className="text-[15px] font-bold">Publicação</h2>

            <div className="space-y-1.5">
              <Label htmlFor="category" className="text-xs font-semibold text-muted-foreground">
                Categoria
              </Label>
              <Select
                value={post.category_id || ""}
                onValueChange={(value) =>
                  setPost((prev) => ({ ...prev, category_id: value || null }))
                }
              >
                <SelectTrigger id="category" className="h-10 rounded-[10px] bg-muted text-[13.5px]">
                  <SelectValue placeholder="Selecione uma categoria" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="slug" className="text-xs font-semibold text-muted-foreground">
                Slug (URL)
              </Label>
              <Input
                id="slug"
                value={post.slug}
                onChange={(e) => setPost((prev) => ({ ...prev, slug: e.target.value }))}
                placeholder="url-do-artigo"
                className="h-10 rounded-[10px] bg-muted font-mono text-[13.5px]"
              />
              <p className="text-xs text-muted-foreground">/blog/{post.slug || "url-do-artigo"}</p>
            </div>

            <div className="flex items-center justify-between pt-1">
              <span className="text-[13px] font-semibold">Publicar imediatamente</span>
              <Switch
                checked={publishNow}
                onCheckedChange={setPublishNow}
                aria-label="Publicar imediatamente"
              />
            </div>
          </div>

          <div className="space-y-3 rounded-2xl border border-border bg-card px-[22px] py-5 shadow-card">
            <h2 className="text-[15px] font-bold">Imagem de capa</h2>

            <label className="block cursor-pointer">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleCoverUpload}
                disabled={uploadingCover}
              />
              <div className="flex h-[120px] items-center justify-center overflow-hidden rounded-xl border border-dashed border-border bg-muted font-mono text-[11px] text-muted-foreground transition-colors hover:border-primary/50">
                {uploadingCover ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> enviando…
                  </span>
                ) : post.cover_image_url ? (
                  <img
                    src={post.cover_image_url}
                    alt="Capa"
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : (
                  "arraste uma imagem 1200×630"
                )}
              </div>
            </label>

            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="h-px flex-1 bg-border" />
              ou
              <div className="h-px flex-1 bg-border" />
            </div>

            <Input
              value={post.cover_image_url}
              onChange={(e) =>
                setPost((prev) => ({
                  ...prev,
                  cover_image_url: e.target.value,
                }))
              }
              placeholder="https://..."
            />
          </div>
        </div>
      </div>
    </div>
  );
}
